  import React, { useEffect, useState } from 'react';
  import { useDispatch, useSelector } from 'react-redux';
  import {
    getActiveLiveSessions,
    getAllLiveSessions,
    startLiveSession,
    endLiveSession,
  } from '../Redux/Dashboard/DashboardSlice';
  import judicialLogo from '../Images/judicialLogo.png';
  import { Notify } from '../CommonPopup/DashboardPopUp';
  import { IoCloseCircleOutline } from 'react-icons/io5';
  import SessionView from './SessionView';
  import '../Style/LiveSession.css';

  // Simple JWT decode function
  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error decoding token:', e);
      return {};
    }
  };

  const LiveSessionPage = () => {
    const dispatch = useDispatch();
    const token = localStorage.getItem('token');
    const decodedToken = decodeToken(token || '');
    const isAdmin = decodedToken.role == "superadmin" || false;

    const dashboardState = useSelector((state) => {
      console.log('Raw Redux state:', state);
      return state.DashboardSlice || { activeLiveSessions: [], allLiveSessions: [], loader: false, error: null };
    });

    const { activeLiveSessions, allLiveSessions, loader, error } = dashboardState;

    const [selectedSession, setSelectedSession] = useState(null);
    const [tab, setTab] = useState('active');
    const [newSessionData, setNewSessionData] = useState({
      title: '',
      description: '',
      youtube_link: '',
    });
    const [showStartSessionModal, setShowStartSessionModal] = useState(false);

    useEffect(() => {
      console.log('Dispatching getActiveLiveSessions and getAllLiveSessions for tab:', tab);
      dispatch(getActiveLiveSessions());
      dispatch(getAllLiveSessions());
    }, [dispatch, tab]); // Add tab to dependency array

    useEffect(() => {
      console.log('Dashboard State:', dashboardState);
      console.log('Active Live Sessions:', activeLiveSessions);
      console.log('All Live Sessions:', allLiveSessions);
    }, [dashboardState, activeLiveSessions, allLiveSessions]);

    const handleStartSession = async () => {
      if (!newSessionData.title || !newSessionData.youtube_link) {
        Notify({ data: 'error', msg: 'Title and YouTube link are required', type: 'error' });
        return;
      }

      try {
        await dispatch(startLiveSession(newSessionData)).unwrap();
        Notify({ data: 'success', msg: 'Session started successfully', type: 'success' });
        setNewSessionData({ title: '', description: '', youtube_link: '' });
        setShowStartSessionModal(false);
        dispatch(getActiveLiveSessions());
        dispatch(getAllLiveSessions());
      } catch (err) {
        Notify({ data: 'error', msg: err.message || 'Failed to start session', type: 'error' });
      }
    };

    const sessionsToDisplay = tab === 'active' ? activeLiveSessions : allLiveSessions;

    return (
      <div className="LiveSessionMain">
        <img className="LiveSessionLogo" src={judicialLogo} alt="Judicial Logo" />
        <h2 className="LiveSessionTitle">Live Sessions</h2>

        {selectedSession ? (
          <SessionView
            session={selectedSession}
            onBack={() => setSelectedSession(null)}
            isAdmin={isAdmin}
          />
        ) : (
          <>
            <div className="LiveSessionControls">
              <div className="LiveSessionTabs">
                <button
                  className={`LiveSessionTabBtn ${tab === 'active' ? 'LiveSessionTabActive' : ''}`}
                  onClick={() => setTab('active')}
                >
                  Active
                </button>
                <button
                  className={`LiveSessionTabBtn ${tab === 'all' ? 'LiveSessionTabActive' : ''}`}
                  onClick={() => setTab('all')}
                >
                  All
                </button>
              </div>

              {isAdmin && (
                <button
                  className="LiveSessionAddBtn"
                  onClick={() => setShowStartSessionModal(true)}
                >
                  <span className="LiveSessionIcon">+</span> Start New Session
                </button>
              )}
            </div>

            <div className="LiveSessionTableContainer">
              {loader ? (
                <div className="LiveSessionSpinner">
                  <p className="LiveSessionSpinnerText">Loading...</p>
                </div>
              ) : error ? (
                <p className="LiveSessionErrorText">Error: {error.message}</p>
              ) : (
                <table className="LiveSessionTable">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Started By</th>
                      <th>Start Time</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsToDisplay.length > 0 ? (
                      sessionsToDisplay.map((session) => (
                        <tr key={session._id}>
                          <td>{session.title}</td>
                          <td>{session.description}</td>
                          <td>{session.started_by?.name || 'Admin'}</td>
                          <td>{new Date(session.start_time).toLocaleString()}</td>
                          <td >
                            <button
                              className="LiveSessionBtn LiveSessionBtnPrimary"
                              onClick={() => setSelectedSession(session)}
                            >
                              {session.is_active && !session.end_time ? 'Join' : 'View Details'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="LiveSessionNoData">
                          No sessions available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Start Session Modal */}
            {showStartSessionModal && (
              <div className="LiveSessionModalOverlay">
                <div className="LiveSessionModalContent">
                  <div className="LiveSessionModalHeader">
                    <h5>Start New Session</h5>
                    <IoCloseCircleOutline
                      size={28}
                      onClick={() => setShowStartSessionModal(false)}
                      style={{ cursor: 'pointer', color: '#FF4D4F' }}
                    />
                  </div>
                  <div className="LiveSessionModalBody">
                    <div className="LiveSessionInputGroup">
                      <label className="LiveSessionLabel">Title</label>
                      <input
                        type="text"
                        value={newSessionData.title}
                        onChange={(e) => setNewSessionData({ ...newSessionData, title: e.target.value })}
                        placeholder="Enter session title"
                        className="LiveSessionInput"
                        required
                      />
                    </div>
                    <div className="LiveSessionInputGroup">
                      <label className="LiveSessionLabel">Description</label>
                      <textarea
                        value={newSessionData.description}
                        onChange={(e) => setNewSessionData({ ...newSessionData, description: e.target.value })}
                        placeholder="Enter session description"
                        className="LiveSessionInput"
                      />
                    </div>
                    <div className="LiveSessionInputGroup">
                      <label className="LiveSessionLabel">YouTube Link</label>
                      <input
                        type="url"
                        value={newSessionData.youtube_link}
                        onChange={(e) => setNewSessionData({ ...newSessionData, youtube_link: e.target.value })}
                        placeholder="Enter YouTube link"
                        className="LiveSessionInput"
                        required
                      />
                    </div>
                  </div>
                  <div className="LiveSessionModalFooter">
                    <button
                      className="LiveSessionBtn LiveSessionBtnSecondary"
                      onClick={() => setShowStartSessionModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="LiveSessionBtn LiveSessionBtnPrimary"
                      onClick={handleStartSession}
                    >
                      Start Session
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  export default LiveSessionPage;